import "@dxos/node-std/globals"

// packages/core/mesh/messaging/src/messenger.ts
import assert from "@dxos/node-std/assert";
import { Context } from "@dxos/context";
import { PublicKey } from "@dxos/keys";
import { log } from "@dxos/log";
import { schema, trace } from "@dxos/protocols";
import { ComplexMap, ComplexSet, exponentialBackoffInterval } from "@dxos/util";
var Messenger = class {
  constructor({ signalManager, retryDelay = 100, timeout = 3e3 }) {
    // { peerId, payloadType } => listeners set
    this._listeners = new ComplexMap(({ peerId, payloadType }) => peerId.toHex() + payloadType);
    // peerId => listeners set
    this._defaultListeners = new ComplexMap(PublicKey.hash);
    this._onAckCallbacks = new ComplexMap(PublicKey.hash);
    this._receivedMessages = new ComplexSet((key) => key.toHex());
    this._closed = true;
    this._instanceId = PublicKey.random().toHex();
    this._signalManager = signalManager;
    this._retryDelay = retryDelay;
    this._timeout = timeout;
    this.open();
  }
  open() {
    if (!this._closed) {
      return;
    }
    log.trace("dxos.mesh.messenger", trace.begin({
      id: this._instanceId,
      parentId: this._traceParent
    }), {
      file: "messenger.ts",
      line: 62,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    this._ctx = new Context({
      onError: (err) => log.catch(err, {}, {
        file: "messenger.ts",
        line: 64,
        scope: this,
        callSite: (f, a) => f(...a)
      })
    });
    this._ctx.onDispose(this._signalManager.onMessage.on(async (message) => {
      log("received message", {
        from: message.author
      }, {
        file: "messenger.ts",
        line: 68,
        scope: this,
        callSite: (f, a) => f(...a)
      });
      await this._handleMessage(message);
    }));
    this._closed = false;
  }
  async close() {
    if (this._closed) {
      return;
    }
    this._closed = true;
    await this._ctx.dispose();
    log.trace("dxos.mesh.messenger", trace.end({
      id: this._instanceId
    }), {
      file: "messenger.ts",
      line: 81,
      scope: this,
      callSite: (f, a) => f(...a)
    });
  }
  async sendMessage({ author, recipient, payload }) {
    assert(!this._closed, "Closed");
    const reliablePayload = {
      messageId: PublicKey.random(),
      payload
    };
    log("sent message", {
      messageId: reliablePayload.messageId,
      author,
      recipient
    }, {
      file: "messenger.ts",
      line: 92,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    const cancelRetry = exponentialBackoffInterval(async () => {
      log("retrying message", {
        messageId: reliablePayload.messageId
      }, {
        file: "messenger.ts",
        line: 96,
        scope: this,
        callSite: (f, a) => f(...a)
      });
      try {
        await this._encodeAndSend({
          author,
          recipient,
          reliablePayload
        });
      } catch (error) {
        log.error("failed to send message", {
          error
        }, {
          file: "messenger.ts",
          line: 100,
          scope: this,
          callSite: (f, a) => f(...a)
        });
      }
    }, this._retryDelay);
    const timeout = setTimeout(() => {
      log("message not delivered", {
        messageId: reliablePayload.messageId
      }, {
        file: "messenger.ts",
        line: 105,
        scope: this,
        callSite: (f, a) => f(...a)
      });
      this._onAckCallbacks.delete(reliablePayload.messageId);
      cancelRetry();
    }, this._timeout);
    assert(!this._onAckCallbacks.has(reliablePayload.messageId));
    this._onAckCallbacks.set(reliablePayload.messageId, () => {
      this._onAckCallbacks.delete(reliablePayload.messageId);
      cancelRetry();
      clearTimeout(timeout);
    });
    this._ctx.onDispose(() => {
      cancelRetry();
      clearTimeout(timeout);
    });
    await this._encodeAndSend({
      author,
      recipient,
      reliablePayload
    });
  }
  /**
  * Subscribes onMessage function to messages that contains payload with payloadType.
  * @param payloadType if not specified, onMessage will be subscribed to all types of messages.
  */
  async listen({ peerId, payloadType, onMessage }) {
    assert(!this._closed, "Closed");
    await this._signalManager.subscribeMessages(peerId);
    let listeners;
    if (!payloadType) {
      listeners = this._defaultListeners.get(peerId);
      if (!listeners) {
        listeners = /* @__PURE__ */ new Set();
        this._defaultListeners.set(peerId, listeners);
      }
    } else {
      listeners = this._listeners.get({
        peerId,
        payloadType
      });
      if (!listeners) {
        listeners = /* @__PURE__ */ new Set();
        this._listeners.set({
          peerId,
          payloadType
        }, listeners);
      }
    }
    listeners.add(onMessage);
    return {
      unsubscribe: async () => {
        listeners.delete(onMessage);
      }
    };
  }
  async _encodeAndSend({ author, recipient, reliablePayload }) {
    await this._signalManager.sendMessage({
      author,
      recipient,
      payload: {
        type_url: "dxos.mesh.messaging.ReliablePayload",
        value: schema.getCodecForType("dxos.mesh.messaging.ReliablePayload").encode(reliablePayload, {
          preserveAny: true
        })
      }
    });
  }
  async _handleMessage(message) {
    switch (message.payload.type_url) {
      case "dxos.mesh.messaging.ReliablePayload": {
        await this._handleReliablePayload(message);
        break;
      }
      case "dxos.mesh.messaging.Acknowledgement": {
        await this._handleAcknowledgement({
          payload: message.payload
        });
        break;
      }
    }
  }
  async _handleReliablePayload({ author, recipient, payload }) {
    assert(payload.type_url === "dxos.mesh.messaging.ReliablePayload");
    const reliablePayload = schema.getCodecForType("dxos.mesh.messaging.ReliablePayload").decode(payload.value, {
      preserveAny: true
    });
    log("handling message", {
      messageId: reliablePayload.messageId
    }, {
      file: "messenger.ts",
      line: 206,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    if (this._receivedMessages.has(reliablePayload.messageId)) {
      return;
    }
    this._receivedMessages.add(reliablePayload.messageId);
    await this._sendAcknowledgement({
      author,
      recipient,
      messageId: reliablePayload.messageId
    });
    await this._callListeners({
      author,
      recipient,
      payload: reliablePayload.payload
    });
  }
  async _handleAcknowledgement({ payload }) {
    var _a;
    assert(payload.type_url === "dxos.mesh.messaging.Acknowledgement");
    (_a = this._onAckCallbacks.get(schema.getCodecForType("dxos.mesh.messaging.Acknowledgement").decode(payload.value).messageId)) == null ? void 0 : _a();
  }
  async _sendAcknowledgement({ author, recipient, messageId }) {
    log("sending ACK", {
      messageId,
      from: recipient,
      to: author
    }, {
      file: "messenger.ts",
      line: 241,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    await this._signalManager.sendMessage({
      author: recipient,
      recipient: author,
      payload: {
        type_url: "dxos.mesh.messaging.Acknowledgement",
        value: schema.getCodecForType("dxos.mesh.messaging.Acknowledgement").encode({
          messageId
        })
      }
    });
  }
  async _callListeners(message) {
    {
      const defaultListenerMap = this._defaultListeners.get(message.recipient);
      if (defaultListenerMap) {
        for (const listener of defaultListenerMap) {
          await listener(message);
        }
      }
    }
    {
      const listenerMap = this._listeners.get({
        peerId: message.recipient,
        payloadType: message.payload.type_url
      });
      if (listenerMap) {
        for (const listener1 of listenerMap) {
          await listener1(message);
        }
      }
    }
  }
};

// packages/core/mesh/messaging/src/signal-client/signal-client.ts
import assert3 from "@dxos/node-std/assert";
import { Event as Event2, Trigger as Trigger2, scheduleTask, synchronized } from "@dxos/async";
import { Context as Context2 } from "@dxos/context";
import { PublicKey as PublicKey2 } from "@dxos/keys";
import { log as log3 } from "@dxos/log";
import { trace as trace2 } from "@dxos/protocols";
import { ComplexMap as ComplexMap2 } from "@dxos/util";

// packages/core/mesh/messaging/src/signal-client/signal-rpc-client.ts
import WebSocket from "isomorphic-ws";
import assert2 from "@dxos/node-std/assert";
import { Trigger, Event } from "@dxos/async";
import { log as log2 } from "@dxos/log";
import { schema as schema2 } from "@dxos/protocols";
import { createProtoRpcPeer } from "@dxos/rpc";
var SignalRPCClient = class {
  // prettier-ignore
  constructor(_url) {
    this._url = _url;
    this._connectTrigger = new Trigger();
    this.connected = new Event();
    this.disconnected = new Event();
    this.error = new Event();
    this._socket = new WebSocket(this._url);
    this._rpc = createProtoRpcPeer({
      requested: {
        Signal: schema2.getService("dxos.mesh.signal.Signal")
      },
      noHandshake: true,
      port: {
        send: (msg) => {
          this._socket.send(msg);
        },
        subscribe: (cb) => {
          this._socket.onmessage = async (msg) => {
            if (typeof Blob !== "undefined" && msg.data instanceof Blob) {
              cb(Buffer.from(await msg.data.arrayBuffer()));
            } else {
              cb(msg.data);
            }
          };
        }
      },
      encodingOptions: {
        preserveAny: true
      }
    });
    this._socket.onopen = async () => {
      try {
        await this._rpc.open();
        log2(`RPC open ${this._url}`, {}, {
          file: "signal-rpc-client.ts",
          line: 62,
          scope: this,
          callSite: (f, a) => f(...a)
        });
        this.connected.emit();
        this._connectTrigger.wake();
      } catch (err) {
        this.error.emit(err);
      }
    };
    this._socket.onclose = async () => {
      log2(`Disconnected ${this._url}`, {}, {
        file: "signal-rpc-client.ts",
        line: 71,
        scope: this,
        callSite: (f, a) => f(...a)
      });
      this.disconnected.emit();
      await this.close();
    };
    this._socket.onerror = (event) => {
      var _a, _b;
      log2.error((_a = event.message) != null ? _a : "Socket error", {
        url: this._url
      }, {
        file: "signal-rpc-client.ts",
        line: 77,
        scope: this,
        callSite: (f, a) => f(...a)
      });
      this.error.emit((_b = event.error) != null ? _b : new Error(event.message));
    };
  }
  async close() {
    var _a, _b;
    try {
      await ((_a = this._rpc) == null ? void 0 : _a.close());
    } catch (err) {
      log2.catch(err, {}, {
        file: "signal-rpc-client.ts",
        line: 86,
        scope: this,
        callSite: (f, a) => f(...a)
      });
    }
    (_b = this._socket) == null ? void 0 : _b.close();
  }
  async join({ topic, peerId }) {
    log2("join", {
      topic,
      peerId
    }, {
      file: "signal-rpc-client.ts",
      line: 92,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    await this._connectTrigger.wait();
    assert2(this._rpc, "Rpc is not initialized");
    const swarmStream = this._rpc.rpc.Signal.join({
      swarm: topic.asUint8Array(),
      peer: peerId.asUint8Array()
    });
    await swarmStream.waitUntilReady();
    return swarmStream;
  }
  async receiveMessages(peerId) {
    log2("receiveMessages", {
      peerId
    }, {
      file: "signal-rpc-client.ts",
      line: 104,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    await this._connectTrigger.wait();
    assert2(this._rpc, "Rpc is not initialized");
    const messageStream = this._rpc.rpc.Signal.receiveMessages({
      peer: peerId.asUint8Array()
    });
    await messageStream.waitUntilReady();
    return messageStream;
  }
  async sendMessage({ author, recipient, payload }) {
    log2("sendMessage", {
      author,
      recipient,
      payload
    }, {
      file: "signal-rpc-client.ts",
      line: 115,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    await this._connectTrigger.wait();
    assert2(this._rpc, "Rpc is not initialized");
    await this._rpc.rpc.Signal.sendMessage({
      author: author.asUint8Array(),
      recipient: recipient.asUint8Array(),
      payload
    });
  }
};

// packages/core/mesh/messaging/src/signal-client/signal-client.ts
var __decorate = function(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function")
    r = Reflect.decorate(decorators, target, key, desc);
  else
    for (var i = decorators.length - 1; i >= 0; i--)
      if (d = decorators[i])
        r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var DEFAULT_RECONNECT_TIMEOUT = 100;
var MAX_RECONNECT_TIMEOUT = 5e3;
var SignalState;
(function(SignalState2) {
  SignalState2[
    /** Connection is being established. */
    "CONNECTING"
  ] = "CONNECTING";
  SignalState2[
    /** Connection is being re-established. */
    "RE_CONNECTING"
  ] = "RE_CONNECTING";
  SignalState2[
    /** Connected. */
    "CONNECTED"
  ] = "CONNECTED";
  SignalState2[
    /** Server terminated the connection. Socket will be reconnected. */
    "DISCONNECTED"
  ] = "DISCONNECTED";
  SignalState2[
    /** Socket was closed. */
    "CLOSED"
  ] = "CLOSED";
})(SignalState || (SignalState = {}));
var SignalClient = class {
  /**
  * @param _host Signal server websocket URL.
  * @param _onMessage
  */
  constructor(_host, _onMessage) {
    this._host = _host;
    this._onMessage = _onMessage;
    this._state = SignalState.CLOSED;
    this._reconnectAfter = DEFAULT_RECONNECT_TIMEOUT;
    this._connectionStarted = new Date();
    this._lastStateChange = new Date();
    this._clientReady = new Trigger2();
    this.statusChanged = new Event2();
    this.commandTrace = new Event2();
    this.swarmEvent = new Event2();
    this._swarmStreams = new ComplexMap2(({ topic, peerId }) => topic.toHex() + peerId.toHex());
    this._messageStreams = new ComplexMap2((key) => key.toHex());
    this._instanceId = PublicKey2.random().toHex();
    this._performance = {
      sentMessages: 0,
      receivedMessages: 0,
      reconnectCounter: 0,
      joinCounter: 0,
      leaveCounter: 0
    };
  }
  open() {
    log3.trace("dxos.mesh.signal-client", trace2.begin({
      id: this._instanceId,
      parentId: this._traceParent
    }), {
      file: "signal-client.ts",
      line: 120,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    if ([
      SignalState.CONNECTED,
      SignalState.CONNECTING
    ].includes(this._state)) {
      return;
    }
    this._initContext();
    this._setState(SignalState.CONNECTING);
    this._createClient();
  }
  async close() {
    log3("closing...", {}, {
      file: "signal-client.ts",
      line: 132,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    if ([
      SignalState.CLOSED
    ].includes(this._state)) {
      return;
    }
    await this._ctx.dispose();
    this._clientReady.reset();
    await this._client.close();
    this._setState(SignalState.CLOSED);
    log3("closed", {}, {
      file: "signal-client.ts",
      line: 142,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    log3.trace("dxos.mesh.signal-client", trace2.end({
      id: this._instanceId,
      data: {
        performance: this._performance
      }
    }), {
      file: "signal-client.ts",
      line: 143,
      scope: this,
      callSite: (f, a) => f(...a)
    });
  }
  getStatus() {
    var _a;
    return {
      host: this._host,
      state: this._state,
      error: (_a = this._lastError) == null ? void 0 : _a.message,
      reconnectIn: this._reconnectAfter,
      connectionStarted: this._connectionStarted,
      lastStateChange: this._lastStateChange
    };
  }
  async join({ topic, peerId }) {
    this._performance.joinCounter++;
    log3("joining", {
      topic,
      peerId
    }, {
      file: "signal-client.ts",
      line: 159,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    await this._clientReady.wait();
    assert3(this._state === SignalState.CONNECTED, "Not connected to Signal Server");
    await this._subscribeSwarmEvents({
      topic,
      peerId
    });
  }
  async leave({ topic, peerId }) {
    var _a;
    this._performance.leaveCounter++;
    log3("leaving", {
      topic,
      peerId
    }, {
      file: "signal-client.ts",
      line: 168,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    (_a = this._swarmStreams.get({
      topic,
      peerId
    })) == null ? void 0 : _a.close();
    this._swarmStreams.delete({
      topic,
      peerId
    });
  }
  async sendMessage(msg) {
    this._performance.sentMessages++;
    await this._clientReady.wait();
    assert3(this._state === SignalState.CONNECTED, "Not connected to Signal Server");
    await this._client.sendMessage(msg);
  }
  async subscribeMessages(peerId) {
    await this._clientReady.wait();
    assert3(this._state === SignalState.CONNECTED, "Not connected to Signal Server");
    if (this._messageStreams.has(peerId)) {
      return {
        unsubscribe: async () => {
          this._messageStreams.get(peerId).close();
          this._messageStreams.delete(peerId);
        }
      };
    }
    const messageStream = await this._client.receiveMessages(peerId);
    messageStream.subscribe(async (message) => {
      this._performance.receivedMessages++;
      await this._onMessage({
        author: PublicKey2.from(message.author),
        recipient: PublicKey2.from(message.recipient),
        payload: message.payload
      });
    });
    if (!this._messageStreams.has(peerId)) {
      this._messageStreams.set(peerId, messageStream);
    }
    this._ctx.onDispose(() => {
      messageStream.close();
      this._messageStreams.delete(peerId);
    });
    return {
      unsubscribe: async () => {
        messageStream.close();
        this._messageStreams.delete(peerId);
      }
    };
  }
  _setState(newState) {
    this._state = newState;
    this._lastStateChange = new Date();
    log3("signal state changed", {
      status: this.getStatus()
    }, {
      file: "signal-client.ts",
      line: 228,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    this.statusChanged.emit(this.getStatus());
  }
  _initContext() {
    this._ctx = new Context2({
      onError: (err) => log3.catch(err, {}, {
        file: "signal-client.ts",
        line: 234,
        scope: this,
        callSite: (f, a) => f(...a)
      })
    });
  }
  _createClient() {
    this._connectionStarted = new Date();
    try {
      this._client = new SignalRPCClient(this._host);
    } catch (err) {
      if (this._state === SignalState.RE_CONNECTING) {
        this._incrementReconnectTimeout();
      }
      this._lastError = err;
      this._setState(SignalState.DISCONNECTED);
      this._reconnect();
    }
    this._client.connected.on(this._ctx, () => {
      this._lastError = void 0;
      this._reconnectAfter = DEFAULT_RECONNECT_TIMEOUT;
      this._setState(SignalState.CONNECTED);
      this._clientReady.wake();
    });
    this._client.error.on(this._ctx, (error) => {
      log3("socket error", {
        error
      }, {
        file: "signal-client.ts",
        line: 261,
        scope: this,
        callSite: (f, a) => f(...a)
      });
      if (this._state === SignalState.CLOSED) {
        return;
      }
      if (this._state === SignalState.RE_CONNECTING) {
        this._incrementReconnectTimeout();
      }
      this._lastError = error;
      this._setState(SignalState.DISCONNECTED);
      this._reconnect();
    });
    this._client.disconnected.on(this._ctx, () => {
      log3("socket disconnected", {}, {
        file: "signal-client.ts",
        line: 276,
        scope: this,
        callSite: (f, a) => f(...a)
      });
      if (this._state !== SignalState.CONNECTING && this._state !== SignalState.RE_CONNECTING) {
        return;
      }
      if (this._state === SignalState.RE_CONNECTING) {
        this._incrementReconnectTimeout();
      }
      this._setState(SignalState.DISCONNECTED);
      this._reconnect();
    });
  }
  _incrementReconnectTimeout() {
    this._reconnectAfter *= 2;
    this._reconnectAfter = Math.min(this._reconnectAfter, MAX_RECONNECT_TIMEOUT);
  }
  _reconnect() {
    this._performance.reconnectCounter++;
    log3(`reconnecting in ${this._reconnectAfter}ms`, {}, {
      file: "signal-client.ts",
      line: 298,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    if (this._state === SignalState.RE_CONNECTING) {
      console.error("Signal api already reconnecting.");
      return;
    }
    if (this._state === SignalState.CLOSED) {
      return;
    }
    this._setState(SignalState.RE_CONNECTING);
    this._ctx.dispose().catch((err) => log3.catch(err, {}, {
      file: "signal-client.ts",
      line: 308,
      scope: this,
      callSite: (f, a) => f(...a)
    }));
    this._initContext();
    scheduleTask(this._ctx, async () => {
      this._client.close().catch(() => {
      });
      this._createClient();
    }, this._reconnectAfter);
  }
  async _subscribeSwarmEvents({ topic, peerId }) {
    assert3(!this._swarmStreams.has({
      topic,
      peerId
    }), "Already subscribed to swarm events.");
    const swarmStream = await this._client.join({
      topic,
      peerId
    });
    swarmStream.subscribe((swarmEvent) => {
      this.swarmEvent.emit({
        topic,
        swarmEvent
      });
    });
    this._swarmStreams.set({
      topic,
      peerId
    }, swarmStream);
    this._ctx.onDispose(() => {
      swarmStream.close();
      this._swarmStreams.delete({
        topic,
        peerId
      });
    });
  }
};
__decorate([
  synchronized
], SignalClient.prototype, "_subscribeSwarmEvents", null);

// packages/core/mesh/messaging/src/signal-manager/memory-signal-manager.ts
import assert4 from "@dxos/node-std/assert";
import { Event as Event3 } from "@dxos/async";
import { Context as Context3 } from "@dxos/context";
import { PublicKey as PublicKey3 } from "@dxos/keys";
import { log as log4 } from "@dxos/log";
import { ComplexMap as ComplexMap3, ComplexSet as ComplexSet2 } from "@dxos/util";
var MemorySignalManagerContext = class {
  constructor() {
    // Swarm messages.
    this.swarmEvent = new Event3();
    // Mapping from topic to set of peers.
    this.swarms = new ComplexMap3(PublicKey3.hash);
    // Map of connections for each peer for signaling.
    this.connections = new ComplexMap3(PublicKey3.hash);
  }
};
var MemorySignalManager = class {
  // prettier-ignore
  constructor(_context) {
    this._context = _context;
    this.statusChanged = new Event3();
    this.commandTrace = new Event3();
    this.swarmEvent = new Event3();
    this.onMessage = new Event3();
    this._joinedSwarms = new ComplexSet2(({ topic, peerId }) => topic.toHex() + peerId.toHex());
    this._ctx = new Context3();
    this._ctx.onDispose(this._context.swarmEvent.on((data) => this.swarmEvent.emit(data)));
  }
  async open() {
    if (!this._ctx.disposed) {
      return;
    }
    this._ctx = new Context3();
    this._ctx.onDispose(this._context.swarmEvent.on((data) => this.swarmEvent.emit(data)));
    await Promise.all([
      ...this._joinedSwarms.values()
    ].map((value) => this.join(value)));
  }
  async close() {
    if (this._ctx.disposed) {
      return;
    }
    const joinedSwarmsCopy = new ComplexSet2(({ topic, peerId }) => topic.toHex() + peerId.toHex(), [
      ...this._joinedSwarms.values()
    ]);
    await Promise.all([
      ...this._joinedSwarms.values()
    ].map((value) => this.leave(value)));
    this._joinedSwarms = joinedSwarmsCopy;
    await this._ctx.dispose();
  }
  getStatus() {
    return [];
  }
  async join({ topic, peerId }) {
    assert4(!this._ctx.disposed, "Closed");
    this._joinedSwarms.add({
      topic,
      peerId
    });
    if (!this._context.swarms.has(topic)) {
      this._context.swarms.set(topic, new ComplexSet2(PublicKey3.hash));
    }
    this._context.swarms.get(topic).add(peerId);
    this._context.swarmEvent.emit({
      topic,
      swarmEvent: {
        peerAvailable: {
          peer: peerId.asUint8Array(),
          since: new Date()
        }
      }
    });
    for (const [topic1, peerIds] of this._context.swarms) {
      Array.from(peerIds).forEach((peerId2) => {
        this.swarmEvent.emit({
          topic: topic1,
          swarmEvent: {
            peerAvailable: {
              peer: peerId2.asUint8Array(),
              since: new Date()
            }
          }
        });
      });
    }
  }
  async leave({ topic, peerId }) {
    assert4(!this._ctx.disposed, "Closed");
    this._joinedSwarms.delete({
      topic,
      peerId
    });
    if (!this._context.swarms.has(topic)) {
      this._context.swarms.set(topic, new ComplexSet2(PublicKey3.hash));
    }
    this._context.swarms.get(topic).delete(peerId);
    const swarmEvent = {
      peerLeft: {
        peer: peerId.asUint8Array()
      }
    };
    this._context.swarmEvent.emit({
      topic,
      swarmEvent
    });
  }
  async sendMessage({ author, recipient, payload }) {
    var _a;
    assert4(recipient);
    assert4(!this._ctx.disposed, "Closed");
    if (!this._context.connections.has(recipient)) {
      log4.warn("recipient is not subscribed for messages", {
        author,
        recipient
      }, {
        file: "memory-signal-manager.ts",
        line: 160,
        scope: this,
        callSite: (f, a) => f(...a)
      });
      return;
    }
    if (!((_a = this._context.connections.get(recipient)) == null ? void 0 : _a._ctx.disposed)) {
      this._context.connections.get(recipient).onMessage.emit({
        author,
        recipient,
        payload
      });
    }
  }
  async subscribeMessages(peerId) {
    log4("subscribing", {
      peerId
    }, {
      file: "memory-signal-manager.ts",
      line: 170,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    this._context.connections.set(peerId, this);
    return {
      unsubscribe: async () => {
        log4("unsubscribing", {
          peerId
        }, {
          file: "memory-signal-manager.ts",
          line: 175,
          scope: this,
          callSite: (f, a) => f(...a)
        });
        this._context.connections.delete(peerId);
      }
    };
  }
};

// packages/core/mesh/messaging/src/signal-manager/websocket-signal-manager.ts
import assert5 from "@dxos/node-std/assert";
import { Event as Event4, DeferredTask, synchronized as synchronized2 } from "@dxos/async";
import { Context as Context4 } from "@dxos/context";
import { PublicKey as PublicKey4 } from "@dxos/keys";
import { log as log5 } from "@dxos/log";
import { trace as trace3 } from "@dxos/protocols";
import { ComplexMap as ComplexMap4, ComplexSet as ComplexSet3 } from "@dxos/util";
var __decorate2 = function(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function")
    r = Reflect.decorate(decorators, target, key, desc);
  else
    for (var i = decorators.length - 1; i >= 0; i--)
      if (d = decorators[i])
        r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var WebsocketSignalManager = class {
  // prettier-ignore
  constructor(_hosts) {
    this._hosts = _hosts;
    this._servers = /* @__PURE__ */ new Map();
    this._topicsJoined = new ComplexMap4((topic) => topic.toHex());
    this._topicsJoinedPerSignal = /* @__PURE__ */ new Map();
    this._subscribedMessages = new ComplexSet3(PublicKey4.hash);
    this._reconcilingLater = false;
    this._opened = false;
    this.statusChanged = new Event4();
    this.commandTrace = new Event4();
    this.swarmEvent = new Event4();
    this.onMessage = new Event4();
    this._instanceId = PublicKey4.random().toHex();
    log5(`Created WebsocketSignalManager with signal servers: ${_hosts}`, {}, {
      file: "websocket-signal-manager.ts",
      line: 59,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    assert5(_hosts.length === 1, "Only a single signaling server connection is supported");
    for (const host of this._hosts) {
      const server = new SignalClient(host, async (message) => this.onMessage.emit(message));
      server._traceParent = this._instanceId;
      server.swarmEvent.on((data) => this.swarmEvent.emit(data));
      server.statusChanged.on(() => this.statusChanged.emit(this.getStatus()));
      this._servers.set(host, server);
      server.commandTrace.on((trace4) => this.commandTrace.emit(trace4));
      this._topicsJoinedPerSignal.set(host, new ComplexMap4(PublicKey4.hash));
    }
    this._initContext();
  }
  async open() {
    if (this._opened) {
      return;
    }
    log5.trace("dxos.mesh.websocket-signal-manager", trace3.begin({
      id: this._instanceId,
      parentId: this._traceParent
    }), {
      file: "websocket-signal-manager.ts",
      line: 81,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    this._initContext();
    [
      ...this._servers.values()
    ].forEach((server) => server.open());
    await Promise.all([
      ...this._servers.values()
    ].map((server) => Promise.all([
      ...this._subscribedMessages.values()
    ].map((peerId) => server.subscribeMessages(peerId)))));
    this._opened = true;
    this._reconcileTask.schedule();
  }
  async close() {
    if (!this._opened) {
      return;
    }
    this._opened = false;
    await this._ctx.dispose();
    await Promise.all(Array.from(this._servers.values()).map((server) => server.close()));
    [
      ...this._topicsJoinedPerSignal.values()
    ].map((value) => value.clear());
    log5.trace("dxos.mesh.websocket-signal-manager", trace3.end({
      id: this._instanceId
    }), {
      file: "websocket-signal-manager.ts",
      line: 106,
      scope: this,
      callSite: (f, a) => f(...a)
    });
  }
  getStatus() {
    return Array.from(this._servers.values()).map((server) => server.getStatus());
  }
  async join({ topic, peerId }) {
    log5(`Join ${topic} ${peerId}`, {}, {
      file: "websocket-signal-manager.ts",
      line: 115,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    assert5(!this._topicsJoined.has(topic), `Topic ${topic} is already joined`);
    assert5(this._opened, "Closed");
    this._topicsJoined.set(topic, peerId);
    this._reconcileTask.schedule();
  }
  async leave({ topic, peerId }) {
    log5("leaving", {
      topic,
      peerId
    }, {
      file: "websocket-signal-manager.ts",
      line: 125,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    assert5(!!this._topicsJoined.has(topic), `Topic ${topic} was not joined`);
    assert5(this._opened, "Closed");
    this._topicsJoined.delete(topic);
    this._reconcileTask.schedule();
  }
  async sendMessage({ author, recipient, payload }) {
    log5(`Signal ${recipient}`, {}, {
      file: "websocket-signal-manager.ts",
      line: 142,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    assert5(this._opened, "Closed");
    await Promise.all([
      ...this._servers.values()
    ].map((server) => server.sendMessage({
      author,
      recipient,
      payload
    }).catch((err) => log5(err, {}, {
      file: "websocket-signal-manager.ts",
      line: 147,
      scope: this,
      callSite: (f, a) => f(...a)
    }))));
  }
  async subscribeMessages(peerId) {
    log5(`Subscribed for message stream peerId=${peerId}`, {}, {
      file: "websocket-signal-manager.ts",
      line: 153,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    assert5(this._opened, "Closed");
    this._subscribedMessages.add(peerId);
    const unsubscribeHandles = await Promise.all([
      ...this._servers.values()
    ].map((signalClient) => signalClient.subscribeMessages(peerId)));
    return {
      unsubscribe: async () => {
        await Promise.all(unsubscribeHandles.map((handle) => handle.unsubscribe()));
        this._subscribedMessages.delete(peerId);
      }
    };
  }
  _initContext() {
    this._ctx = new Context4({
      onError: (err) => log5.catch(err, {}, {
        file: "websocket-signal-manager.ts",
        line: 172,
        scope: this,
        callSite: (f, a) => f(...a)
      })
    });
    this._reconcileTask = new DeferredTask(this._ctx, async () => {
      await this._reconcileJoinedTopics();
    });
  }
  async _reconcileJoinedTopics() {
    log5("Reconciling..", {}, {
      file: "websocket-signal-manager.ts",
      line: 181,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    for (const [host, server] of this._servers) {
      const actualJoinedTopics = this._topicsJoinedPerSignal.get(host);
      for (const [topic, actualPeerId] of actualJoinedTopics) {
        try {
          const desiredPeerId = this._topicsJoined.get(topic);
          if (!desiredPeerId || !desiredPeerId.equals(actualPeerId)) {
            await server.leave({
              topic,
              peerId: actualPeerId
            });
            actualJoinedTopics.delete(topic);
          }
        } catch (err) {
          log5.error(`Error leaving swarm: ${err}`, {}, {
            file: "websocket-signal-manager.ts",
            line: 195,
            scope: this,
            callSite: (f, a) => f(...a)
          });
          this._reconcileTask.schedule();
        }
      }
      for (const [topic1, desiredPeerId1] of this._topicsJoined) {
        try {
          const actualPeerId1 = actualJoinedTopics.get(topic1);
          if (!actualPeerId1) {
            await server.join({
              topic: topic1,
              peerId: desiredPeerId1
            });
            actualJoinedTopics.set(topic1, desiredPeerId1);
          } else {
            if (!actualPeerId1.equals(desiredPeerId1)) {
              throw new Error(`Joined with peerId different from desired: ${JSON.stringify({
                actualPeerId: actualPeerId1,
                desiredPeerId: desiredPeerId1
              })}`);
            }
          }
        } catch (err1) {
          log5.error(`Error joining swarm: ${err1}`, {}, {
            file: "websocket-signal-manager.ts",
            line: 218,
            scope: this,
            callSite: (f, a) => f(...a)
          });
          this._reconcileTask.schedule();
        }
      }
    }
    log5("Done reconciling..", {}, {
      file: "websocket-signal-manager.ts",
      line: 223,
      scope: this,
      callSite: (f, a) => f(...a)
    });
  }
};
__decorate2([
  synchronized2
], WebsocketSignalManager.prototype, "open", null);
__decorate2([
  synchronized2
], WebsocketSignalManager.prototype, "join", null);
__decorate2([
  synchronized2
], WebsocketSignalManager.prototype, "leave", null);
export {
  MemorySignalManager,
  MemorySignalManagerContext,
  Messenger,
  SignalClient,
  SignalState,
  WebsocketSignalManager
};
//# sourceMappingURL=index.mjs.map
